import { ibanValidation } from "@shared-kernel/primary/forms/validations/iban";
import { number, object, string } from "yup";
import { REQUIRED_FIELD } from "@shared-kernel/primary/forms/constants";
import { siretValidation } from "@shared-kernel/primary/forms/validations/siret";
import { activityRegistrationNumberValidation } from "@shared-kernel/primary/forms/validations/activity-registration-number";
import { addressLookupSchema } from "@shared-kernel/primary/forms/validations/postal-address.schema";
import { EUROPEAN_COUNTRIES, SUPPORTED_COUNTRY } from "@shared-kernel/domain/types/supported-country";
import { postalAddressToAddressLookup } from "@shared-kernel/domain/types/postal-address";
import { MAX_HOURLY_PRICE, TEACHER_MIN_HOURLY_PRICE } from "@academy-context/write/domain/constants/shared";
import { STRUCTURE_TYPE } from "@user-management-context/read/domain/types/admin/teacher";
import type { Nullable } from "@shared-kernel/core/types/nullable";
import type { AddressLookup } from "@shared-kernel/domain/types/address-lookup";
import type { TeacherVM, UpdateTeacherProfessionalInfoBody } from "@shared-kernel/application/ports/teacher/teacher-repository";
import { formatActivityRegistrationNumber, formatSiret, normalizeIban, normalizeIdentifier, normalizeSiret } from "@utils/formatting";

export interface TeacherProfileFormInputs {
  defaultPrice: Nullable<number>;
  siret: string;
  iban: string;
  activityRegistrationNumber: string;
  structureType: STRUCTURE_TYPE;
  businessName: string;
  postalAddress: AddressLookup;
  addressComplement: string;
  country: SUPPORTED_COUNTRY;
}

export const defaultValues: TeacherProfileFormInputs = {
  defaultPrice: null,
  siret: "",
  iban: "",
  activityRegistrationNumber: "",
  structureType: STRUCTURE_TYPE.AUTO_ENTREPRISE,
  businessName: "",
  postalAddress: { placeId: "", description: "", sessionToken: "", invalid: false },
  addressComplement: "",
  country: SUPPORTED_COUNTRY.FRANCE,
};

export const schema = object().shape({
  defaultPrice: number()
    .typeError(`Minimum ${TEACHER_MIN_HOURLY_PRICE}€`)
    .positive(`Minimum ${TEACHER_MIN_HOURLY_PRICE}€`)
    .min(TEACHER_MIN_HOURLY_PRICE, `Minimum ${TEACHER_MIN_HOURLY_PRICE}€`)
    .max(MAX_HOURLY_PRICE, `Maximum ${MAX_HOURLY_PRICE}€`)
    .required(REQUIRED_FIELD),
  siret: siretValidation(),
  iban: ibanValidation(),
  activityRegistrationNumber: activityRegistrationNumberValidation(),
  structureType: string().required(REQUIRED_FIELD).oneOf(Object.values(STRUCTURE_TYPE)),
  businessName: string().when("structureType", {
    is: (val: STRUCTURE_TYPE) => val === STRUCTURE_TYPE.ASSOCIATION || val === STRUCTURE_TYPE.ENTREPRISE,
    then: schema => schema.required("La raison sociale est requise").max(255),
    otherwise: schema => schema.optional(),
  }),
  postalAddress: addressLookupSchema,
  addressComplement: string().max(255).defined(),
  country: string().required(REQUIRED_FIELD).oneOf(EUROPEAN_COUNTRIES, "Le pays doit être en Europe"),
});

export const formatTeacherDataToFormData = (teacher: TeacherVM): TeacherProfileFormInputs => ({
  ...defaultValues,
  defaultPrice: teacher.defaultPrice ?? defaultValues.defaultPrice,
  siret: teacher.siret ? formatSiret(teacher.siret) : defaultValues.siret,
  iban: teacher.iban ?? defaultValues.iban,
  activityRegistrationNumber: teacher.activityRegistrationNumber
    ? formatActivityRegistrationNumber(teacher.activityRegistrationNumber)
    : defaultValues.activityRegistrationNumber,
  structureType: teacher.structureType,
  businessName: teacher.businessName ?? defaultValues.businessName,
  ...(teacher.postalAddress
    ? {
        postalAddress: postalAddressToAddressLookup(teacher.postalAddress),
        addressComplement: teacher.postalAddress.addressComplement ?? defaultValues.addressComplement,
        country: teacher.postalAddress.country || defaultValues.country,
      }
    : {}),
});

export const formatFormDataToBodyData = (data: TeacherProfileFormInputs): UpdateTeacherProfessionalInfoBody => {
  if (data.defaultPrice === null) throw new Error("Teacher default price is required");

  return {
    defaultPrice: data.defaultPrice,
    siret: normalizeSiret(data.siret, data.country) || null,
    iban: data.iban ? normalizeIban(data.iban) : null,
    activityRegistrationNumber: normalizeIdentifier(data.activityRegistrationNumber) || null,
    structureType: data.structureType,
    businessName: data.businessName || null,
    postalAddress: data.postalAddress,
    addressComplement: data.addressComplement || null,
  };
};
